import { useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import { useDispatch } from "react-redux";
import { compose } from "redux";

import { useTranslate } from "@similarweb/i18n-provider";
import { IconButton, Tooltip } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import { useMount } from "app/custom-hooks/useMount";
import withSecondaryBarSet from "app/pages/sales-intelligence/hoc/withSecondaryBarSet";
import { useCrmEnrichmentContext } from "app/pages/sales-intelligence/modules/crm-enrichment/contexts/CrmEnrichmentContext";
import { CRM_INTEGRATION_MANUAL_URLS } from "app/pages/sales-intelligence/modules/manage-integrations/constants/crm";
import { useDisconnectCRMIntegrationMutation } from "app/pages/sales-intelligence/modules/manage-integrations/query/mutations/useDisconnectCRMIntegrationMutation";
import manageIntegrationsTrackingService from "app/pages/sales-intelligence/modules/manage-integrations/services/tracking";
import withCrmEnrichmentProvider from "app/pages/sales-intelligence/pages/manage-integration/hocs/withCrmEnrichmentProvider";
import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";
import siNavigator from "app/pages/sales-intelligence/services/siNavigator";
import integrationTrackingService from "app/pages/sales-intelligence/services/tracking/integrationTrackingService";
import HelpButton from "app/pages/sales-intelligence/sub-modules/account-overview/shared/components/HelpButton/HelpButton";
import { fetchContactsStatusThunk } from "app/pages/sales-intelligence/sub-modules/contacts-tab/store/effects";
import EducationBarContainer from "app/pages/sales-intelligence/sub-modules/education-bar/containers/EducationBarContainer";
import useEducationBarState from "app/pages/sales-intelligence/sub-modules/education-bar/hooks/useEducationBarState";

import { INTEGRATION_HEADLINE } from "../../common-components/export-table/translations";
import useCrmEnrichmentQuota from "../../modules/quota/hooks/useCrmEnrichmentQuota";
import { withCacheToken } from "../manage-export/hoc/withCacheToken";
import { StyledManagementHeader } from "../management/components/Management/styles";
import DisconnectModal from "../management/components/ManagementSections/components/Modals/DisconnectModal";
import { useHasManagementAccess } from "../management/hooks/useHasManagementAccess";
import { MANAGEMENT_SF_DISCONNECT_TOOLTIP } from "../management/translations/salesforceKeys";

import CrmFiltersSettings from "./components/AccountTab/CrmFiltersSettings";
import { CrmSyncSettings } from "./components/CrmSyncSettings";
import ExportPreferences from "./components/ExportPreferences/ExportPreferences";
import { StyledIntegrationSetting } from "./components/IntegrationSetting/styles";
import { ManageIntegrationBreadcrumbs } from "./components/ManageIntegrationBreadcrumbs/ManageIntegrationBreadcrumbs";
import IntegrationSettingsHeader from "./components/ManageIntegrationSettingsHeader/IntegrationSettingsHeader";
import { SalesforceActivitySettings } from "./components/SalesforceActivitySettings";
import { AccountStatusSettings } from "./components/ShowInCrmToggle";
import { DynamicsEnrichmentComingSoon } from "./components/DynamicsEnrichmentComingSoon/DynamicsEnrichmentComingSoon";
import HsEnrichmentPreferences from "./containers/EnrichmentPreferences/HsEnrichmentPreferences";
import SfEnrichmentPreferences from "./containers/EnrichmentPreferences/SfEnrichmentPreferences";
import { withConnectionCheck } from "./hocs/withConnectionCheck";
import { withMappingsAndColumnsFetch } from "./hocs/withMappingsAndColumnsFetch";
import { useIsLoadingExportPreferences } from "./hooks/useIsLoadingExportPreferences";
import {
    managementIntegrationDynamicsStorage,
    managementIntegrationHsStorage,
    managementIntegrationSfStorage,
} from "./services/storage/storage";
import {
    StyledHeader,
    StyledHeaderButton,
    StyledManagementIntegrationContainer,
    StyledManagementSectionsContainer,
    StyledManagementWrapper,
} from "./styles/styles";

const ManageIntegration = () => {
    const dispatch = useDispatch();
    const { translate } = useTranslate();

    const { crmProvider: integrationName } = useCrmEnrichmentContext();

    const { state } = useCurrentStateAndParams();

    const { isAvailableHubspotEnrichment, isAvailableSalesforceEnrichment } = useCrmEnrichmentQuota();

    const isLoading = useIsLoadingExportPreferences();

    const disconnectMutation = useDisconnectCRMIntegrationMutation(integrationName);
    const [hasAccess] = useHasManagementAccess(integrationName);

    const [isDisconnectModalOpen, setIsDisconnectModalOpen] = useState(false);

    const isCrmIntegrationsEnabled = salesSettingsHelper.isCrmIntegrationsEnabled();
    const isDisconnectVisible = hasAccess && isCrmIntegrationsEnabled;

    const handleOpenDisconnect = () => setIsDisconnectModalOpen(true);
    const handleCloseDisconnectModal = () => setIsDisconnectModalOpen(false);
    const handleConfirmDisconnect = () => {
        disconnectMutation.mutate();
        setIsDisconnectModalOpen(false);
    };

    useMount(() => {
        void dispatch(fetchContactsStatusThunk());
    });

    const educationBarStorageMap = {
        hubspot: managementIntegrationHsStorage,
        salesforce: managementIntegrationSfStorage,
        dynamics: managementIntegrationDynamicsStorage,
    };

    const { isBarVisible, onClose, onOpen } = useEducationBarState(
        educationBarStorageMap[integrationName] ?? managementIntegrationSfStorage,
    );

    const isEducationBarEnabled = integrationName !== "dynamics";

    const isSfEnrichmentSectionVisible =
        salesSettingsHelper.isSalesforceEnrichmentEnabled &&
        isAvailableSalesforceEnrichment &&
        integrationName === "salesforce";

    const handleEditMapping = (): void => {
        integrationTrackingService.trackMapFieldsModal(integrationName);

        void siNavigator.goToExportIntegrationPage({ crm: integrationName });
    };

    const handleNavigateToAllCRMs = (): void => {
        void siNavigator.goToCrmIntegrationsPage();
    };

    const handleGuideClick = (): void => {
        manageIntegrationsTrackingService.trackCrmGuideClick(integrationName);
    };

    return (
        <StyledManagementWrapper>
            {isEducationBarEnabled && isBarVisible ? (
                <EducationBarContainer
                    onClose={onClose}
                    stateId={state.stateId || ""}
                />
            ) : null}
            <ManageIntegrationBreadcrumbs integrationName={integrationName}>
                <Flex
                    alignItems="center"
                    gap="8px"
                >
                    {CRM_INTEGRATION_MANUAL_URLS[integrationName] ? (
                        <a
                            href={CRM_INTEGRATION_MANUAL_URLS[integrationName]}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <IconButton
                                iconName="helpcenter"
                                type="flat"
                                data-automation={`${integrationName}-manage-integration.guide-button`}
                                onClick={handleGuideClick}
                            />
                        </a>
                    ) : null}

                    {isEducationBarEnabled && !isBarVisible ? <HelpButton onClick={onOpen} /> : null}
                </Flex>
            </ManageIntegrationBreadcrumbs>
            <StyledManagementSectionsContainer data-automation={`${integrationName}-sections`}>
                <StyledHeader>
                    <StyledManagementHeader>{translate(INTEGRATION_HEADLINE[integrationName])}</StyledManagementHeader>

                    <Flex
                        alignItems="center"
                        gap="8px"
                    >
                        {isDisconnectVisible ? (
                            <Tooltip
                                placement="top"
                                content={translate(MANAGEMENT_SF_DISCONNECT_TOOLTIP)}
                            >
                                <div>
                                    <IconButton
                                        data-automation={`${integrationName}-manage-integration.disconnect-button`}
                                        type="flatDark"
                                        iconName="disconnect"
                                        iconSize="sm"
                                        isDisabled={disconnectMutation.isPending}
                                        onClick={handleOpenDisconnect}
                                    >
                                        {translate("si.common.disconnect")}
                                    </IconButton>
                                </div>
                            </Tooltip>
                        ) : null}

                        <StyledHeaderButton
                            type="flat"
                            onClick={handleNavigateToAllCRMs}
                        >
                            {translate("si.management.integration.connect-other-crm")}
                        </StyledHeaderButton>
                    </Flex>
                </StyledHeader>

                {salesSettingsHelper.isCrmListEnabled() ? (
                    <StyledManagementIntegrationContainer
                        noPaddingBottom
                        data-automation={`${integrationName}-import-settings`}
                    >
                        <IntegrationSettingsHeader />
                        <CrmSyncSettings />
                        <StyledIntegrationSetting data-automation={`${integrationName}-crm-filters-setting`}>
                            <CrmFiltersSettings integrationName={integrationName} />
                        </StyledIntegrationSetting>
                        {integrationName === "salesforce" ? <SalesforceActivitySettings /> : null}
                        <AccountStatusSettings />
                    </StyledManagementIntegrationContainer>
                ) : (
                    <StyledManagementIntegrationContainer data-automation={`${integrationName}-crm-filters-section`}>
                        <CrmFiltersSettings integrationName={integrationName} />
                    </StyledManagementIntegrationContainer>
                )}
                <StyledManagementIntegrationContainer data-automation={`${integrationName}-export-preferences`}>
                    <ExportPreferences
                        integrationName={integrationName}
                        onEditMapping={handleEditMapping}
                        isEditDisabled={isLoading}
                    />
                </StyledManagementIntegrationContainer>

                {integrationName === "hubspot" && isAvailableHubspotEnrichment ? (
                    <StyledManagementIntegrationContainer data-automation={`${integrationName}-enrichment`}>
                        <HsEnrichmentPreferences
                            integrationName={integrationName}
                            onEditMapping={handleEditMapping}
                            isMappingLoading={isLoading}
                        />
                    </StyledManagementIntegrationContainer>
                ) : null}

                {isSfEnrichmentSectionVisible ? (
                    <StyledManagementIntegrationContainer data-automation={`${integrationName}-enrichment`}>
                        <SfEnrichmentPreferences
                            integrationName={integrationName}
                            onEditMapping={handleEditMapping}
                            isMappingLoading={isLoading}
                        />
                    </StyledManagementIntegrationContainer>
                ) : null}

                {integrationName === "dynamics" ? (
                    <StyledManagementIntegrationContainer>
                        <DynamicsEnrichmentComingSoon />
                    </StyledManagementIntegrationContainer>
                ) : null}
            </StyledManagementSectionsContainer>

            <DisconnectModal
                integrationName={integrationName}
                onConfirm={handleConfirmDisconnect}
                isOpen={isDisconnectModalOpen}
                onClose={handleCloseDisconnectModal}
            />
        </StyledManagementWrapper>
    );
};

const ManageIntegrationPage = compose(
    withCrmEnrichmentProvider,
    withConnectionCheck,
    withMappingsAndColumnsFetch({ refetchIfExists: true }),
    withSecondaryBarSet("SalesIntelligenceManagement"),
    withCacheToken,
)(ManageIntegration);

export default ManageIntegrationPage;
